import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  IconArrowLeft, IconBox, IconCalendarClock, IconDots, IconFileCheck, IconFlame, IconHelpCircle, IconMail, IconPlant2, IconPlus, IconReceipt, IconRoute, IconSearch, IconShield, IconShieldCheck, IconShip, IconTable, IconTrash, IconTruck, IconUsers,
  type Icon,
} from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { catLabel, commentCount, filterCatalog, filterCodes, filterQa, firstUnclear, GROUPS, openUnclear, QA_CATS, QA_ST } from './catalogLogic';
import type { QaCat, QaStatus, SvcCatalog } from './catalogTypes';
import { useWide } from './useWide';
import s from './catalog.module.css';
import './catalog.i18n';

// Mint-ServiceCatalog (1536 / 1920): 3 tab Giới thiệu dịch vụ · Mã dịch vụ · Hỏi đáp. Chỉ đọc; nút ghi báo toast.
// Giá niêm yết 20' / 40' của mã chỉ hiện khi `?money=view` (board: money=view) và không phải noperm.

type Tab = 'intro' | 'code' | 'qa';
const ICON: Record<string, Icon> = { FRT: IconShip, TRF: IconTruck, CCF: IconShieldCheck, SCY: IconBox, COO: IconFileCheck, PTF: IconPlant2, FUM: IconFlame, ISR: IconShield, DDL: IconMail };
const ME = 'DAVID';

/** Rộng cột bảng mã theo board (CW 1264): STT 64 · Mã 90 · VN auto · EN 230 · Nhóm 100 · Đơn vị 80 · VAT 70 · Vận tải 80 · Giá 190 · xoá 50. */
const COLS = [
  { k: 'stt', w: 64 }, { k: 'code', w: 90 }, { k: 'vi', w: 0 }, { k: 'en', w: 230 }, { k: 'grp', w: 100 }, { k: 'unit', w: 80 },
  { k: 'vat', w: 70, right: true }, { k: 'truck', w: 80 },
] as const;

const params = () => new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
function readState(): ScreenState | null {
  const v = params().get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function Catalog() {
  const tr = useT();
  const toast = useToast();
  const nav = useNavigate();
  const wide = useWide();
  const svc = useList('svcCatalog', { limit: 100 });
  const codesQ = useList('svcCode', { limit: 500 });
  const qaQ = useList('svcQa', { limit: 500 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('intro');
  const [q, setQ] = useState('');
  const [grp, setGrp] = useState<string>('all');
  const [qSvc, setQSvc] = useState('all');
  const [qCat, setQCat] = useState<'all' | QaCat>('all');
  const [qSt, setQSt] = useState<'all' | QaStatus>('all');

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (svc.isLoading || codesQ.isLoading || qaQ.isLoading) state = 'loading';
  else if (svc.isError || codesQ.isError || qaQ.isError) state = [svc.error, codesQ.error, qaQ.error].some((e) => e instanceof DataError && e.code === 'noperm') ? 'noperm' : 'error';
  else if ((svc.data?.items.length ?? 0) === 0) state = 'empty';
  const noperm = state === 'noperm';
  const ready = state === 'ready' || noperm;
  const showPrice = !noperm && params().get('money') === 'view';

  const services = svc.data?.items ?? [];
  const codes = codesQ.data?.items ?? [];
  const qaAll = qaQ.data?.items ?? [];
  const visQa = useMemo(() => qaAll.filter((x) => !noperm || x.st !== 'draft' || x.who === ME), [qaAll, noperm]);

  const cards = useMemo(() => filterCatalog(services, q), [services, q]);
  const codeRows = useMemo(() => filterCodes(codes, q, grp), [codes, q, grp]);
  const qaBase = useMemo(() => filterQa(visQa, q), [visQa, q]);
  const qaRows = qaBase.filter((x) => (qSvc === 'all' || x.svc === qSvc) && (qCat === 'all' || x.cat === qCat) && (qSt === 'all' || x.st === qSt));

  const noWrite = () => toast({ msg: tr('cat.noWrite') });
  const soon = (what: string) => () => toast({ msg: tr('cat.soon', { what }) });
  const pickTab = (k: string) => {
    setTab(k as Tab);
    setQ('');
  };

  const chips =
    tab === 'code'
      ? [{ key: 'all', label: tr('cat.all'), n: codes.length }, ...GROUPS.map((g) => ({ key: g as string, label: g as string, n: codes.filter((c) => c.grp === g).length }))]
      : tab === 'qa'
        ? ([['all', tr('cat.all')], ['pub', tr('cat.pub')], ['draft', tr('cat.draft')], ['ask', tr('cat.asks')]] as const).map(([k, label]) => ({ key: k as string, label, n: k === 'all' ? qaBase.length : qaBase.filter((x) => x.st === k).length }))
        : [];
  const chipOn = tab === 'code' ? grp : qSt;
  const pickChip = (k: string) => (tab === 'code' ? setGrp(k) : setQSt(k as 'all' | QaStatus));
  const sum = tab === 'intro' ? tr('cat.nSvc', { n: cards.length }) : tab === 'code' ? tr('cat.nCode', { a: codeRows.length, b: codes.length }) : tr('cat.nQa', { n: qaRows.length });
  const ph = tab === 'intro' ? tr('cat.phIntro') : tab === 'code' ? tr('cat.phCode') : tr('cat.phQa');

  const asks = visQa.filter((x) => x.st === 'ask');
  const unclear = qaAll.filter((x) => openUnclear(x.id) > 0);
  const pct = (w: number) => `${((w / 1264) * 100).toFixed(3)}%`;

  const dockEl = (
          <aside className={s.dock}>
            <section aria-label={tr('cat.asks')}>
              <div className={s.dkH}><h2>{tr('cat.asks')}</h2><span className={s.lb}>{tr('cat.nAsks', { n: asks.length })}</span></div>
              {asks.map((a) => (
                <button key={a.id} type="button" className={s.dkItem} onClick={soon(tr('cat.caseDetail'))}>
                  <b><IconHelpCircle size={16} aria-hidden="true" />{a.title}</b>
                  <div className={s.lb}>{a.svc} · {a.who}</div>
                </button>
              ))}
            </section>
            <section aria-label={tr('cat.unclear')}>
              <div className={s.dkH}><h2>{tr('cat.unclearOpen')}</h2><span className={s.lb}>{tr('cat.nPts', { n: unclear.length })}</span></div>
              {unclear.map((a) => (
                <button key={a.id} type="button" className={`${s.dkItem} ${s.dkWarn}`} onClick={soon(tr('cat.caseDetail'))}>
                  <b><IconCalendarClock size={16} aria-hidden="true" />{a.title}</b>
                  <div className={s.tx2}>{firstUnclear(a.id)}</div>
                </button>
              ))}
            </section>
            <section aria-label={tr('cat.recent')}>
              <h2 className={s.dkH2}>{tr('cat.recentH')}</h2>
              {RECENT.map(([code, k, when]) => (
                <div key={code} className={s.dkRec}>
                  <b className={s.codeTx}>{code}</b>
                  <span className={s.ell}>{k}</span>
                  <span className={s.lb}>{when}</span>
                </div>
              ))}
            </section>
          </aside>
  );

  return (
    <Page kind="table" dock={wide && ready ? dockEl : undefined}>
      <PageHeader
        title={tr('cat.title')}
        lead={
          <a className={s.back} href="/ezy/service-tasks" aria-label={tr('cat.back')} title={tr('cat.back')}>
            <IconArrowLeft size={18} stroke={1.9} />
          </a>
        }
        tabs={
          <Segmented
            label={tr('cat.section')}
            value={tab}
            onChange={pickTab}
            items={[
              { key: 'intro', label: tr('cat.tIntro'), n: ready ? services.length : undefined },
              { key: 'code', label: tr('cat.tCode'), n: ready ? codes.length : undefined },
              { key: 'qa', label: tr('cat.tQa'), n: ready ? visQa.length : undefined },
            ]}
          />
        }
        actions={
          <>
            <HeaderTool label={tr('cat.otherOps')} icon={IconDots} onClick={soon(tr('cat.opsMenu'))} />
            {tab === 'intro' && (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => void nav({ to: '/ezy/quotes/edit' })}>
                {tr('cat.newQuote')}
              </Button>
            )}
            {tab === 'code' && (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={noperm} onClick={noWrite}>
                {tr('cat.addCode')}
              </Button>
            )}
            {tab === 'qa' && (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
                {tr('cat.addCase')}
              </Button>
            )}
          </>
        }
      />
        <div className={s.page}>
          {state !== 'loading' && (
            <div className={s.tb}>
              <label className={s.search}>
                <IconSearch size={16} aria-hidden="true" />
                <input aria-label={tr('cat.search')} placeholder={ph} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              {ready && chips.length > 0 && (
                <div className={s.chips} role="group" aria-label={tab === 'code' ? tr('cat.codeGroup') : tr('cat.status')}>
                  {chips.map((c) => (
                    <button key={c.key} type="button" className={`${s.chip} ${chipOn === c.key ? s.chipOn : ''}`} aria-pressed={chipOn === c.key} onClick={() => pickChip(c.key)}>
                      {c.label} <span className={s.lb}>{c.n}</span>
                    </button>
                  ))}
                </div>
              )}
              {ready && <span className={s.sum}>{sum}</span>}
            </div>
          )}
          <StateView
            state={state === 'noperm' ? 'ready' : state}
            title={state === 'empty' ? tr('cat.empty') : undefined}
            desc={state === 'empty' ? tr('cat.emptyDesc') : undefined}
            icon={state === 'empty' ? <IconTable size={26} stroke={1.8} /> : undefined}
            action={state === 'empty' ? (
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
                {tr('cat.addCode')}
              </Button>
            ) : state === 'error' ? <Button onClick={() => void Promise.all([svc.refetch(), codesQ.refetch(), qaQ.refetch()])}>{tr('cat.retry')}</Button> : undefined}
            skeleton={<Skeleton rows={9} />}
          >
            {tab === 'intro' && (
              <>
                <section className={s.grid} aria-label={tr('cat.offered')}>
                  {cards.map((c) => (
                    <ServiceCard key={c.id} c={c} nCode={codes.filter((x) => c.codes.includes(x.code)).length} nQa={visQa.filter((x) => x.svc === c.code).length} onOpen={soon(tr('cat.svcDetail'))} />
                  ))}
                </section>
                {cards.length === 0 && <div className={s.none}>{tr('cat.noSvc', { q })}</div>}
              </>
            )}
            {tab === 'code' && (
              <section className={s.card} aria-label={tr('cat.codeList')}>
                <table className={s.table}>
                  <colgroup>
                    {COLS.map((c) => (
                      <col key={c.k} style={{ width: c.w ? pct(c.w) : 'auto' }} />
                    ))}
                    {showPrice && <col style={{ width: pct(190) }} />}
                    <col style={{ width: pct(50) }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {COLS.map((c) => (
                        <th key={c.k} scope="col" className={'right' in c && c.right ? s.right : undefined}>{tr(`cat.col.${c.k}`)}</th>
                      ))}
                      {showPrice && <th scope="col" className={s.right}>{tr('cat.price')}</th>}
                      <th scope="col"><span className={s.sr}>{tr('cat.del')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {codeRows.map((r) => (
                      <tr key={r.id} className={r.active ? undefined : s.off} onClick={soon(tr('cat.editCode'))}>
                        <td className={s.lb}>{r.ord}</td>
                        <td><b className={s.codeTx}>{r.code}</b></td>
                        <td className={s.ell}>
                          {r.vi}
                          {!r.active && <span className={s.offTag}>{tr('cat.inactive')}</span>}
                        </td>
                        <td className={`${s.ell} ${s.tx2}`}>{r.en}</td>
                        <td>
                          <span className={`${s.pill} ${s[`g${r.grp}`]}`}>
                            <span className={s.dot} aria-hidden="true" />
                            {r.grp}
                          </span>
                        </td>
                        <td className={s.tx2}>{r.unit}</td>
                        <td className={s.right}>{r.vat}%</td>
                        <td className={s.tx2}>{r.truck ? tr('cat.yes') : '—'}</td>
                        {showPrice && <td className={`${s.right} ${s.fw}`}>{r.p20 || '—'} / {r.p40 || '—'}</td>}
                        <td className={s.right}>
                          <button type="button" className={s.del} aria-label={tr('cat.delCode', { code: r.code })} title={tr('cat.delCodeT')} disabled={noperm} onClick={(e) => { e.stopPropagation(); noWrite(); }}>
                            <IconTrash size={15} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {codeRows.length === 0 && <div className={s.none}>{tr('cat.noCode')}</div>}
                <div className={s.foot}>{tr('cat.cosNote')}</div>
              </section>
            )}
            {tab === 'qa' && (
              <div className={s.qa}>
                <aside className={s.qaSide} aria-label={tr('cat.qaFilter')}>
                  <div className={s.qaH}>{tr('cat.hSvc')}</div>
                  <Opt on={qSvc === 'all'} k={tr('cat.allSvc')} n={qaBase.length} pick={() => setQSvc('all')} />
                  {services.map((v) => (
                    <Opt key={v.id} on={qSvc === v.code} k={`${v.code} · ${v.name}`} n={qaBase.filter((x) => x.svc === v.code).length} pick={() => setQSvc(v.code)} />
                  ))}
                  <div className={s.qaH}>{tr('cat.hCat')}</div>
                  <Opt on={qCat === 'all'} k={tr('cat.allCat')} n={qaBase.length} pick={() => setQCat('all')} />
                  {QA_CATS.map(([k, label]) => (
                    <Opt key={k} on={qCat === k} k={label} n={qaBase.filter((x) => x.cat === k).length} pick={() => setQCat(k)} />
                  ))}
                </aside>
                <section className={s.qaList} aria-label={tr('cat.qaList')}>
                  {qaRows.map((x) => {
                    const st = QA_ST[x.st];
                    const open = openUnclear(x.id);
                    return (
                      <button key={x.id} type="button" className={s.qaRow} onClick={soon(tr('cat.caseDetail'))}>
                        <span className={`${s.qaIc} ${x.st === 'ask' ? s.qaAsk : ''}`}>{x.st === 'ask' ? '?' : 'Q'}</span>
                        <div className={s.qaBody}>
                          <div className={s.qaTop}>
                            <span className={s.svcTag}>{x.svc}</span>
                            <b className={s.ell}>{x.title}</b>
                          </div>
                          <div className={`${s.qaSum} ${s.tx2}`}>{x.sum}</div>
                          <div className={`${s.qaMeta} ${s.lb}`}>
                            <span>{catLabel(x.cat)}</span>
                            <span>{tr('cat.by', { who: x.who })}</span>
                            <span>{tr('cat.nComments', { n: commentCount(x.id) })}</span>
                            {open > 0 && <span className={s.warnTx}>{tr('cat.nUnclear', { n: open })}</span>}
                          </div>
                        </div>
                        <span className={`${s.pill} ${s[`t_${st.tone}`]}`}>
                          <span className={s.dot} aria-hidden="true" />
                          {st.label}
                        </span>
                      </button>
                    );
                  })}
                  {qaRows.length === 0 && <div className={s.none}>{tr('cat.noCase')}</div>}
                </section>
              </div>
            )}
          </StateView>
        </div>
    </Page>
  );
}

const RECENT = [['TLX', 'Sửa tên EN · Telex release fee', '21/Oct'], ['PWC', 'Thêm · Phí cắm điện cont lạnh', '17/Oct'], ['XLH', 'Bật Dùng cho vận tải', '09/Oct']] as const;

function Opt({ on, k, n, pick }: { on: boolean; k: string; n: number; pick: () => void }) {
  return (
    <button type="button" className={`${s.opt} ${on ? s.optOn : ''}`} aria-pressed={on} onClick={pick}>
      {k}
      <span className={s.lb}>{n}</span>
    </button>
  );
}

function ServiceCard({ c, nCode, nQa, onOpen }: { c: SvcCatalog; nCode: number; nQa: number; onOpen: () => void }) {
  const tr = useT();
  const Ic = ICON[c.code] ?? IconRoute;
  return (
    <button type="button" className={s.sc} aria-label={tr('cat.view', { name: c.name })} onClick={onOpen}>
      <div className={s.scHead}>
        <span className={`${s.scIc} ${s[`t_${c.tone}`]}`}><Ic size={20} stroke={1.9} aria-hidden="true" /></span>
        <div className={s.scTt}>
          <div className={s.scName}><span className={s.svcTag}>{c.code}</span><b>{c.name}</b></div>
          <div className={`${s.lb} ${s.sm}`}>{c.en}</div>
        </div>
      </div>
      <div className={`${s.tx2} ${s.scDesc}`}>{c.desc}</div>
      <div className={s.scChips}>
        <span title={tr('cat.owner', { v: c.dept })}><IconUsers size={13} aria-hidden="true" />{c.dept}</span>
        <span title={c.mode}><IconTable size={13} aria-hidden="true" />{c.mode}</span>
        <span title={tr('cat.anchor', { v: c.anchor })}><IconCalendarClock size={13} aria-hidden="true" />{c.anchor}</span>
      </div>
      <div className={`${s.scFoot} ${s.lb} ${s.sm}`}>
        <span title={tr('cat.nFee', { n: nCode })}><IconReceipt size={14} aria-hidden="true" /><b>{nCode}</b></span>
        <span title={tr('cat.nSteps', { n: c.steps.length })}><IconRoute size={14} aria-hidden="true" /><b>{c.steps.length}</b></span>
        <span title={tr('cat.nQaT', { n: nQa })}><IconHelpCircle size={14} aria-hidden="true" /><b>{nQa}</b></span>
      </div>
    </button>
  );
}
